import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import * as api from '@/lib/api'
import type { AdminGroup, AdminMember, AdminNewsArticle, AdminScheduleEvent } from '@/lib/api'
import { Users, User, Newspaper, Video, CalendarDays } from 'lucide-react'
import { normalizeImageKitUrl } from '@lic/media-urls'

interface DashboardData {
  groups: AdminGroup[]
  membersByGroup: Record<string, AdminMember[]>
  totalMembers: number
  news: AdminNewsArticle[]
  totalMedia: number
  schedule: AdminScheduleEvent[]
}

function isLightColor(hex: string): boolean {
  const h = hex.replace('#', '')
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return (r * 299 + g * 587 + b * 114) / 1000 > 160
}

const STAT_CARDS = [
  { key: 'groups',   label: 'Groups',   icon: Users,        getValue: (d: DashboardData) => d.groups.length },
  { key: 'members',  label: 'Members',  icon: User,         getValue: (d: DashboardData) => d.totalMembers },
  { key: 'articles', label: 'Articles', icon: Newspaper,    getValue: (d: DashboardData) => d.news.length },
  { key: 'media',    label: 'Media',    icon: Video,        getValue: (d: DashboardData) => d.totalMedia },
  { key: 'events',   label: 'Events',   icon: CalendarDays, getValue: (d: DashboardData) => d.schedule.length },
]

export default function DashboardPage() {
  const { token, isGroupManager, managedGroupSlug } = useAuth()
  const navigate = useNavigate()
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!token) return
    setLoading(true)
    try {
      const [groups, members, news, media, schedule] = await Promise.all([
        api.getAdminGroups(token),
        api.getAdminMembers(token),
        api.getAdminNews(token),
        api.getAdminMedia(token),
        api.getAdminSchedule(token),
      ])

      const membersByGroup = members.reduce<Record<string, AdminMember[]>>((acc, m) => {
        ;(acc[m.group_slug] ??= []).push(m)
        return acc
      }, {})

      setData({ groups, membersByGroup, totalMembers: members.length, news, totalMedia: media.length, schedule })
      setError(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { load() }, [load])

  if (loading) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <div className="h-5 w-28 bg-zinc-800 rounded animate-pulse" />
          <div className="h-3 w-40 bg-zinc-800 rounded animate-pulse mt-1.5" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="bg-zinc-900 rounded-xl border border-zinc-800 p-5 animate-pulse">
              <div className="h-8 w-8 rounded-lg bg-zinc-800 mb-3" />
              <div className="h-7 w-14 bg-zinc-800 rounded mb-2" />
              <div className="h-3 w-20 bg-zinc-800 rounded" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-xl p-5 h-32 bg-zinc-800 animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return <div className="p-6"><p className="text-red-400 text-sm">{error}</p></div>
  }

  if (!data) return null

  return (
    <div className="p-6">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-lg font-semibold text-white">Dashboard</h1>
        <p className="text-zinc-500 text-xs mt-0.5">Overview semua konten</p>
      </div>

      {/* ── Section 1: Stat Cards ───────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
        {STAT_CARDS.map(({ key, label, icon: Icon, getValue }) => (
          <div key={key} className="bg-zinc-900 rounded-xl border border-zinc-800 p-5">
            <div className="mb-3">
              <div className="p-2 rounded-lg bg-zinc-800 inline-flex">
                <Icon size={18} className="text-zinc-400" />
              </div>
            </div>
            <p className="text-2xl font-bold text-white">{getValue(data)}</p>
            <p className="text-xs text-zinc-500 mt-1">{label}</p>
          </div>
        ))}
      </div>

      {/* ── Section 2: Members (group manager) or Groups (admin) ────────── */}
      {isGroupManager ? (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-white">Members</h2>
            <Link to="/members" className="text-xs text-zinc-400 hover:text-white transition-colors">
              View all →
            </Link>
          </div>
          {(() => {
            const myMembers = managedGroupSlug ? (data.membersByGroup[managedGroupSlug] ?? []) : []
            return myMembers.length === 0 ? (
              <p className="text-zinc-500 text-sm py-8 text-center">No members yet.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {myMembers.map((member) => (
                  <div
                    key={member.id}
                    className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden hover:border-zinc-700 transition-colors cursor-pointer"
                    onClick={() => navigate(`/members/${member.id}`, { state: { member } })}
                  >
                    <div className="aspect-square bg-zinc-800 overflow-hidden flex items-center justify-center">
                      {member.image_url ? (
                        <img
                          src={normalizeImageKitUrl(member.image_url)}
                          alt={member.stage_name}
                          className="w-full h-full object-cover object-top"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                        />
                      ) : (
                        <span className="text-zinc-600 text-3xl font-bold">{member.stage_name[0]}</span>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-medium text-white truncate">{member.stage_name}</p>
                      <p className="text-xs text-zinc-500 mt-0.5">{member.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            )
          })()}
        </div>
      ) : (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-white">Groups</h2>
            <Link to="/groups" className="text-xs text-zinc-400 hover:text-white transition-colors">
              View all →
            </Link>
          </div>

          {data.groups.length === 0 ? (
            <p className="text-zinc-500 text-sm py-8 text-center">No groups yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.groups.map((group) => {
                const groupMembers = data.membersByGroup[group.slug] ?? []
                const light = isLightColor(group.color_primary || '#3f3f46')
                return (
                  <div
                    key={group.id}
                    className="relative rounded-xl overflow-hidden cursor-pointer h-44 hover:scale-[1.02] transition-transform"
                    style={{
                      backgroundColor: group.color_primary || '#3f3f46',
                      ...(group.cover_image_url && {
                        backgroundImage: `url(${normalizeImageKitUrl(group.cover_image_url)})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }),
                    }}
                    onClick={() => navigate(`/members?group=${group.slug}`)}
                  >
                    <div
                      className="absolute inset-0"
                      style={{
                        background: group.cover_image_url
                          ? `linear-gradient(to top, ${group.color_primary}dd, ${group.color_primary}44, transparent)`
                          : 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)',
                      }}
                    />
                    <div className="relative h-full flex flex-col justify-between p-4">
                      {groupMembers.length > 0 && (
                        <div className="flex -space-x-2">
                          {groupMembers.slice(0, 5).map((m) => (
                            <img
                              key={m.id}
                              src={normalizeImageKitUrl(m.image_url)}
                              alt={m.stage_name}
                              className="w-7 h-7 rounded-full border-2 border-white/30 object-cover bg-zinc-700"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                            />
                          ))}
                          {groupMembers.length > 5 && (
                            <div className="w-7 h-7 rounded-full bg-black/40 border-2 border-white/30 flex items-center justify-center text-[10px] font-medium text-white">
                              +{groupMembers.length - 5}
                            </div>
                          )}
                        </div>
                      )}
                      <div>
                        <h3 className={`font-semibold text-base leading-tight ${light ? 'text-zinc-900' : 'text-white'}`}>{group.name}</h3>
                        <p className={`text-xs mt-0.5 truncate ${light ? 'text-zinc-700' : 'text-white/70'}`}>{group.tagline}</p>
                        <p className={`text-[10px] mt-1.5 ${light ? 'text-zinc-600' : 'text-white/50'}`}>
                          {groupMembers.length} members · Debut {group.debut_date}
                        </p>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Section 3: Recent Content ───────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent News */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-white">Recent News</h2>
            <Link to="/news" className="text-xs text-zinc-400 hover:text-white transition-colors">
              View all →
            </Link>
          </div>
          {data.news.length === 0 ? (
            <p className="text-zinc-500 text-xs py-4 text-center">No articles yet.</p>
          ) : (
            <div>
              {data.news.slice(0, 5).map((article) => (
                <div key={article.id} className="py-3 border-b border-zinc-800 last:border-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[10px] tracking-wide px-2 py-0.5 rounded bg-zinc-800 text-zinc-300">
                      {article.category}
                    </span>
                    {article.is_featured && <span className="text-yellow-400 text-xs">★</span>}
                  </div>
                  <p className="text-sm text-white truncate">{article.title}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">{article.date}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Schedule */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-white">Schedule</h2>
            <Link to="/schedule" className="text-xs text-zinc-400 hover:text-white transition-colors">
              View all →
            </Link>
          </div>
          {data.schedule.length === 0 ? (
            <p className="text-zinc-500 text-xs py-4 text-center">No events yet.</p>
          ) : (
            <div>
              {data.schedule.slice(0, 5).map((event) => (
                <div key={event.id} className="flex items-center gap-3 py-3 border-b border-zinc-800 last:border-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium truncate">{event.name}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{event.date} · {event.group_slug}</p>
                  </div>
                  <span className="text-[10px] tracking-wide px-2 py-0.5 rounded bg-zinc-800 text-zinc-300 flex-shrink-0">
                    {event.badge}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
